// The builder's Fog tab (item 11, the approved mockups): fog on or off, how it clears, the
// starting light (Shrink the light, Clear), the key cells, how the draft solves through its fog
// (where it stops, step by step) and the rules' words for it.
import type { DraftFogReport } from '@zinc/shared';
import type { ReactNode } from 'react';
import { describeKey, REVEALS, type FogModel, type Reveal } from '../../lib/builder/fog';
import { Switch } from './Palette';

const heading = 'm-0 text-[11px] font-bold tracking-[0.06em] text-muted uppercase';
const button =
  'h-11 rounded-btn border border-line bg-surface px-3.5 text-[14px] font-semibold text-ink disabled:opacity-45';

export interface FogView {
  /** Show where the solve through the fog stops, or step `step` of it; none: the start. */
  stop: boolean;
  step: number | null;
}

function rc(cell: [number, number]) {
  return `r${cell[0] + 1}c${cell[1] + 1}`;
}

export function FogPanel({
  model,
  onFog,
  onReveal,
  report,
  busy,
  error,
  shrinking,
  onShrink,
  onClearLight,
  selectedKey,
  onSelectKey,
  onRemoveKey,
  onAddKey,
  view,
  onView,
  plainScore,
  rules,
}: {
  model: FogModel | null;
  onFog: (on: boolean) => void;
  onReveal: (r: Reveal) => void;
  report: DraftFogReport | null;
  busy: boolean;
  error: string | null;
  shrinking: boolean;
  onShrink: () => void;
  onClearLight: () => void;
  selectedKey: number | null;
  onSelectKey: (i: number | null) => void;
  onRemoveKey: (i: number) => void;
  onAddKey: () => void;
  view: FogView;
  onView: (v: FogView) => void;
  /** The score with everything in view, to compare. */
  plainScore: number | null;
  /** The rules text's sentence about the fog. */
  rules: string;
}) {
  const fogSwitch = (
    <div className="flex flex-col gap-0.5">
      <Switch label="Fog" checked={model !== null} onChange={onFog} />
      <span className="text-[13px] text-subtle">
        The grid starts covered; correct digits clear it.
      </span>
    </div>
  );
  if (!model) return <div className="flex flex-col gap-4">{fogSwitch}</div>;
  const usesKeys = model.reveal !== 'around' || model.keys.length > 0;
  const steps = report?.steps.length ?? 0;
  return (
    <div className="flex flex-col gap-4">
      {fogSwitch}

      <section className="flex flex-col gap-2">
        <h4 className={heading}>How the fog clears</h4>
        <div role="radiogroup" aria-label="How the fog clears" className="flex flex-col">
          {REVEALS.map((r) => (
            <label key={r.key} className="flex min-h-9 items-center gap-2.5 text-[14px]">
              <input
                type="radio"
                name="fog-reveal"
                checked={model.reveal === r.key}
                onChange={() => onReveal(r.key)}
              />
              {r.label}
            </label>
          ))}
        </div>
        {model.reveal === 'held' && (
          <p className="m-0 text-[13px] text-subtle">
            Paint the held-back cells with Hold back: no digit around them clears them; a key cell
            can.
          </p>
        )}
      </section>

      <section className="flex flex-col gap-2">
        <h4 className={heading}>Starting light</h4>
        <p className="m-0 text-[14px] leading-[1.45]">
          {model.visible.length} cell{model.visible.length === 1 ? '' : 's'} lit. Paint them with
          the Light tool.
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={shrinking || !report?.solved}
            title={report?.solved ? '' : 'The light can shrink once the puzzle solves through it.'}
            onClick={onShrink}
            className={button}
          >
            {shrinking ? 'Shrinking…' : 'Shrink the light'}
          </button>
          <button
            type="button"
            disabled={model.visible.length === 0}
            onClick={onClearLight}
            className={button}
          >
            Clear
          </button>
        </div>
      </section>

      {usesKeys && (
        <section className="flex flex-col gap-2">
          <h4 className={heading}>Key cells</h4>
          {model.keys.length === 0 && (
            <p className="m-0 text-[13px] text-subtle">
              None yet. A key cell lights the cells you paint for it once its digit is correct; a
              group lights them once all its digits are.
            </p>
          )}
          <div className="flex flex-col gap-1.5">
            {model.keys.map((k, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <button
                  type="button"
                  aria-pressed={selectedKey === i}
                  onClick={() => onSelectKey(selectedKey === i ? null : i)}
                  className={`flex min-h-11 flex-1 items-center gap-2 rounded-[10px] border px-3 text-left text-[14px] ${selectedKey === i ? 'border-accent bg-accent-tint text-accent-dark' : 'border-line bg-surface text-ink'}`}
                >
                  <span className="flex-1">{describeKey(k)}</span>
                  {selectedKey === i && <span className="text-[12px] font-semibold">Editing</span>}
                </button>
                <button
                  type="button"
                  aria-label={`Remove ${describeKey(k)}`}
                  onClick={() => onRemoveKey(i)}
                  className="h-11 w-11 rounded-[10px] border border-line bg-surface text-[18px] text-muted"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <button type="button" onClick={onAddKey} className={`${button} self-start`}>
            Add a key cell
          </button>
        </section>
      )}

      <section
        className={`flex flex-col gap-2 rounded-[12px] p-3 ${report?.solved ? 'bg-solved-bg' : report && !report.note ? 'bg-warn-bg' : 'bg-field'}`}
      >
        <h4
          className={`m-0 text-[11px] font-bold tracking-[0.06em] uppercase ${report?.solved ? 'text-solved' : report && !report.note ? 'text-warn-ink' : 'text-muted'}`}
        >
          Through the fog
        </h4>
        <p className="m-0 text-[14px] leading-[1.45]">
          {error
            ? `The solve through the fog failed: ${error}`
            : !report || busy
              ? 'Solving through the fog…'
              : report.note
                ? report.note
                : report.solved
                  ? `Solves through the fog in ${steps} steps${report.score ? `: score ${report.score.score.toFixed(1)}` : ''}${plainScore !== null ? ` (${plainScore.toFixed(1)} with everything shown)` : ''}.${report.hiddenGivens.length ? ` ${report.hiddenGivens.length} given${report.hiddenGivens.length === 1 ? ' starts' : 's start'} hidden; each lights before anything needs it.` : ''}`
                  : report.timedOut
                    ? `Still going after ${steps} steps when its time ran out.`
                    : report.unsound
                      ? `The solve went wrong: ${report.unsound}`
                      : !report.hiddenGivensHold
                        ? `A given that starts in the fog is needed before it lights (${report.hiddenGivens.map(rc).join(', ')}): light it from the start, or let something light it sooner.`
                        : `Stops after ${steps} steps, ${report.lit} cells lit.${report.stop ? ` With everything in view the next step is ${report.stop.text.replace(/\.$/, '')}${report.stop.fogged.length ? `; it needs ${report.stop.fogged.join(', ')}, still in the fog` : ''}.` : ''}`}
        </p>
        {report && !report.note && !busy && (
          <div className="flex flex-wrap gap-2">
            {!report.solved && report.stop && (
              <button
                type="button"
                aria-pressed={view.stop}
                onClick={() => onView({ stop: !view.stop, step: null })}
                className={button}
              >
                {view.stop ? 'Show the start' : 'Show where it stops'}
              </button>
            )}
            {steps > 0 && view.step === null && (
              <button
                type="button"
                onClick={() => onView({ stop: false, step: 0 })}
                className={button}
              >
                Step through
              </button>
            )}
          </div>
        )}
        {report && view.step !== null && steps > 0 && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <button
                type="button"
                aria-label="Previous step"
                disabled={view.step === 0}
                onClick={() => onView({ stop: false, step: Math.max(0, view.step! - 1) })}
                className="h-11 w-11 rounded-btn border border-line bg-surface text-[16px] text-ink disabled:opacity-45"
              >
                ‹
              </button>
              <span className="flex-1 text-center text-[14px]">
                Step {view.step + 1} of {steps}
              </span>
              <button
                type="button"
                aria-label="Next step"
                disabled={view.step >= steps - 1}
                onClick={() => onView({ stop: false, step: Math.min(steps - 1, view.step! + 1) })}
                className="h-11 w-11 rounded-btn border border-line bg-surface text-[16px] text-ink disabled:opacity-45"
              >
                ›
              </button>
              <button
                type="button"
                onClick={() => onView({ stop: false, step: null })}
                className="h-11 rounded-btn border border-line bg-surface px-3 text-[13px] text-ink"
              >
                Done
              </button>
            </div>
            <p className="m-0 text-[14px]">
              {report.steps[view.step]!.name}
              {report.steps[view.step]!.placed.length > 0 &&
                `: ${report.steps[view.step]!.placed.map(([c, d]) => `${d} in ${rc(c)}`).join(', ')}`}
            </p>
          </div>
        )}
      </section>

      <section className="flex flex-col gap-1.5">
        <h4 className={heading}>Rules text</h4>
        <p className="m-0 text-[14px] leading-[1.45] text-secondary">“{rules}”</p>
        <p className="m-0 text-[12px] text-muted">
          Written into the rules with the constraints (the Rules tab).
        </p>
      </section>
    </div>
  );
}

export type FogTool = 'light' | 'held' | 'key';

/** The fog's tools at the top of the palette: Light, Key cell, and Hold back when cells are held
 * back. */
export function FogTools({
  reveal,
  tool,
  onTool,
}: {
  reveal: Reveal;
  tool: FogTool | null;
  onTool: (t: FogTool | null) => void;
}) {
  const tools: { key: FogTool; label: string; icon: ReactNode }[] = [
    {
      key: 'light',
      label: 'Light',
      icon: (
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
          <rect
            x="2"
            y="2"
            width="14"
            height="14"
            rx="2"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <path d="M9 5v8M5 9h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      ),
    },
    {
      key: 'key',
      label: 'Key cell',
      icon: (
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
          <circle cx="9" cy="9" r="6" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="9" cy="9" r="2" fill="currentColor" />
        </svg>
      ),
    },
  ];
  if (reveal === 'held')
    tools.push({
      key: 'held',
      label: 'Hold back',
      icon: (
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
          <rect
            x="3"
            y="3"
            width="12"
            height="12"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeDasharray="3 2"
          />
        </svg>
      ),
    });
  return (
    <div className="flex flex-col gap-1.5">
      <h3 className={heading}>Fog</h3>
      <div className="grid grid-cols-2 gap-1.5">
        {tools.map((t) => (
          <button
            key={t.key}
            type="button"
            aria-pressed={tool === t.key}
            onClick={() => onTool(tool === t.key ? null : t.key)}
            className={`flex h-11 items-center gap-2 rounded-[9px] border px-2 text-left text-[13px] ${tool === t.key ? 'border-accent bg-accent-tint font-semibold text-accent-dark' : 'border-transparent bg-field font-medium text-ink'}`}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>
      {tool === 'light' && (
        <p className="m-0 text-[12px] text-muted">
          Tap or drag over cells to light them from the start; again to put them back in the fog.
        </p>
      )}
      {tool === 'key' && (
        <p className="m-0 text-[12px] text-muted">
          Key cell: tap the cell (or drag over several, for a group), then paint the cells it
          lights.
        </p>
      )}
      {tool === 'held' && (
        <p className="m-0 text-[12px] text-muted">
          Tap or drag over the cells no digit around them clears.
        </p>
      )}
    </div>
  );
}
